import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { SpanishRegulatoryHelperService } from '@frontend/promo-homewidget';
import { DSComponents, DesignSystemHelperService } from '@frontend/promo-utils';
import { DsButton } from '@frontend/ui/button';
import { DsCard } from '@frontend/ui/card';
import { DeviceService, DynamicHtmlDirective, PlainLinkComponent } from '@frontend/vanilla/core';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { GrayoutStyleDirective } from '../../../../common/directives/grayout-style.directive';
import { OpenLinkDirective } from '../../../../common/directives/open-link-directive';
import { OnClickWTDirective } from '../../../../common/web-tracking/onclick-web-tracking.directive';
import { WebAnalyticModel } from '../../../../common/web-tracking/web-analytic.model';
import { PromoCountDownTimerDirective } from '../directives/count-down-timer/count-down-timer.directive';
import { CommonEventService } from '../events/common-event.service';
import { OptInContent } from '../models/opt-in-content';
import { OptInDetails } from '../models/opt-in-details';
import {
    TargetPromoState,
    commonContentMessages,
    isGeneral,
    optInContent,
    optInDetails,
    promoEndTime,
    promoStartTime,
} from '../store/target-promotion.selectors';
import { loadTargetPromotionData, userOptedInForPromotion } from '../store/target-promotions.actions';
import { TargetPromotionService } from '../target-promotion-service/target-promotion.service';

interface OptInViewModel {
    optInDetails: OptInDetails;
    optInContent: OptInContent;
    commonContentMessages: { [key: string]: string };
    promoStarted: boolean;
    promoEnded: boolean;
    countDownTime: Date;
    webAnalyticModel: WebAnalyticModel;
}

@Component({
    selector: 'tp-opt-in',
    templateUrl: './opt-in.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LetDirective,
        NgClass,
        NgStyle,
        DynamicHtmlDirective,
        PlainLinkComponent,
        GrayoutStyleDirective,
        OpenLinkDirective,
        OnClickWTDirective,
        PromoCountDownTimerDirective,
        DsButton,
        DsCard,
    ],
})
export class PromoOptInComponent implements OnInit {
    private _promoId: string;
    private _optInClickSubject = new Subject<boolean>();
    @Input() isRankingV2Enabled: string;
    private _promoDateTime$ = combineLatest([this.store.select(promoStartTime), this.store.select(promoEndTime)]).pipe(
        filter(([promoStartTime, promoEndTime]) => promoStartTime !== undefined && promoEndTime !== undefined),
        map(([promoStartTime, promoEndTime]) => {
            const date = new Date();
            const promoStarted = promoStartTime <= date;
            return {
                promoStarted,
                promoEnded: promoEndTime <= date,
                countDownTime: promoStarted ? promoEndTime : promoStartTime,
            };
        }),
    );

    private _isOptInAllowed$ = this.store.select(isGeneral).pipe(
        switchMap((isGeneral) => {
            return this.spanishRegulatoryHelperService.isOptInAllowed$(isGeneral);
        }),
    );

    isOptInAllowed: boolean;
    vm$: Observable<OptInViewModel> = combineLatest([
        this._promoDateTime$,
        this.store.select(optInDetails),
        this.store.select(optInContent),
        this.store.select(commonContentMessages),
        this._isOptInAllowed$,
        this.commonEventService.getWAModel(),
    ]).pipe(
        filter(
            ([promoDateTime, optInDetails, optInContent, commonContentMessages, isOptInAllowed]) =>
                promoDateTime !== undefined &&
                optInDetails !== undefined &&
                optInContent !== undefined &&
                commonContentMessages !== undefined &&
                isOptInAllowed !== undefined,
        ),
        map(([promoDateTime, optInDetails, optInContent, commonContentMessages, isOptInAllowed, webAnalyticModel]) => {
            this.isOptInAllowed = isOptInAllowed;
            return {
                optInDetails,
                optInContent,
                commonContentMessages: commonContentMessages ?? {},
                promoStarted: promoDateTime.promoStarted,
                promoEnded: promoDateTime.promoEnded,
                countDownTime: promoDateTime.countDownTime,
                webAnalyticModel,
            };
        }),
    );
    dsOptinButtonConfig: DSComponents;
    dsOptinConfig: any;
    dsPlayNowButtonConfig: DSComponents;
    dsComponents: DSComponents;

    constructor(
        public device: DeviceService,
        private store: Store<{ targetPromotionModuleData: TargetPromoState }>,
        private route: ActivatedRoute,
        private commonEventService: CommonEventService,
        private spanishRegulatoryHelperService: SpanishRegulatoryHelperService,
        private designSystemHelperService: DesignSystemHelperService,
        private targetPromotionService: TargetPromotionService,
    ) {}

    ngOnInit() {
        this.dsPlayNowButtonConfig = this.designSystemHelperService.getDsComponentsList();
        this.dsOptinButtonConfig = this.designSystemHelperService.getDsComponentsList('optin');
        this.getButtonCss();
        this._promoId = this.route?.snapshot?.parent?.paramMap?.get('promoId') ?? '';
        this.dsComponents = this.designSystemHelperService.getDsComponentsList();
        this.subscribeToOptInClick();
        this.commonEventService.isOptinLoaded.next(true);
    }

    getButtonCss() {
        this.dsOptinConfig = {
            size: this.dsOptinButtonConfig?.dsButton?.size ?? this.dsPlayNowButtonConfig?.dsButton?.size,
            kind: this.dsOptinButtonConfig?.dsButton?.kind ?? this.dsPlayNowButtonConfig?.dsButton?.kind,
            variant: this.dsOptinButtonConfig?.dsButton?.variant ?? this.dsPlayNowButtonConfig?.dsButton?.variant,
        };
    }

    optedInClicked() {
        if (!this.isOptInAllowed) {
            this.spanishRegulatoryHelperService.showNonComplianceErrorMessage(true);
            return;
        }
        this._optInClickSubject.next(true);
    }

    subscribeToOptInClick() {
        this._optInClickSubject
            .pipe(
                distinctUntilChanged(),
                tap(() => {
                    this.targetPromotionService.optinClick$.set(true);
                    this.store.dispatch(userOptedInForPromotion({ promoId: this._promoId }));
                }),
            )
            .subscribe();
    }

    onTimeOut() {
        this.store.dispatch(loadTargetPromotionData({ promoId: this._promoId }));
    }
}
